import React from "react";
import { Link } from "react-router-dom";
import { Briefcase, Clock, ClipboardList, CheckCircle2, Users, Wallet, Plus, IndianRupee, ArrowRight } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatCard } from "../../components/shared/StatCard";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { companyNav } from "../../components/shared/navConfig";
import { companyStats, submittedTasks, candidates } from "../../data/mockData";

export default function CompanyDashboard() {
  return (
    <DashboardShell
      nav={companyNav}
      title="Meridian Textiles"
      subtitle="Company workspace · GST-verified"
      headerRight={
        <Link to="/company/post" data-testid="post-task-header-btn" className="btn-primary px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5">
          <Plus size={13}/> Post task
        </Link>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
        <StatCard testId="stat-active-tasks"      label="Active tasks"      value={companyStats.activeTasks}      delta="+2"   icon={Briefcase} accent/>
        <StatCard testId="stat-pending-approval"  label="Pending approval"  value={companyStats.pendingApproval}  delta="+1"   icon={Clock}/>
        <StatCard testId="stat-applications"      label="Applications"      value={companyStats.applications}     delta="+12"  icon={ClipboardList}/>
        <StatCard testId="stat-allocated"         label="Allocated"         value={companyStats.allocatedTasks}   delta="+1"   icon={Users}/>
        <StatCard testId="stat-completed"         label="Completed"         value={companyStats.completedTasks}   delta="+4"   icon={CheckCircle2}/>
        <StatCard testId="stat-total-spend"       label="Total spend"       value={`₹${(companyStats.totalSpend/1000).toFixed(0)}k`} delta="+18%" icon={Wallet} accent/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* My tasks */}
        <div className="lg:col-span-2 card-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">My tasks</div>
              <h3 className="font-display text-lg text-white mt-1">Recent submissions</h3>
            </div>
            <Link to="/company/tasks" className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">View all <ArrowRight size={12}/></Link>
          </div>
          <table className="table-stack w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 text-left">
                <th className="pb-3 font-normal">Task</th>
                <th className="pb-3 font-normal">Location</th>
                <th className="pb-3 font-normal">Budget</th>
                <th className="pb-3 font-normal">Deadline</th>
                <th className="pb-3 font-normal text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {submittedTasks.map(t => (
                <tr key={t.id} data-testid={`company-task-row-${t.id}`} className="border-t transition-colors duration-200 hover:bg-white/[0.02]" style={{ borderColor: "var(--border)" }}>
                  <td className="py-3">
                    <div className="text-white text-sm">{t.title}</div>
                    <div className="text-[11px] text-zinc-500 font-mono">{t.id}</div>
                  </td>
                  <td data-label="Location" className="py-3 text-zinc-400 text-xs"><span>{t.location}</span></td>
                  <td data-label="Budget" className="py-3 text-white font-mono text-xs"><span>₹{t.budget.toLocaleString()}</span></td>
                  <td data-label="Deadline" className="py-3 text-zinc-400 text-xs"><span>{t.deadline}</span></td>
                  <td data-label="Status" className="py-3 text-right"><StatusBadge status={t.status}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Allocated students */}
        <div className="card-surface p-5">
          <div className="eyebrow">Allocated students</div>
          <h3 className="font-display text-lg text-white mt-1 mb-4">Currently working</h3>
          <div className="space-y-3">
            {candidates.slice(0, 3).map(c => (
              <div key={c.id} className="flex items-center gap-3 p-3 rounded-md border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <img alt={c.name} src={c.avatar} className="w-9 h-9 rounded-full object-cover"/>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{c.name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{c.education}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-blue-400 font-mono">{c.matchScore}%</div>
                  <div className="text-[10px] text-zinc-500">match</div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/workroom" data-testid="company-open-workroom-btn" className="btn-ghost w-full py-2 mt-5 rounded-md text-xs inline-flex items-center justify-center gap-2">
            Open workrooms <ArrowRight size={12}/>
          </Link>
        </div>
      </div>
    </DashboardShell>
  );
}
